import type { FlipkartAdsCampaign } from '@/api/client';

export type FlipkartCampaignSummary = {
  total: number;
  totalDailyBudget: number;
  byStatus: Array<{ label: string; count: number }>;
  byType: Array<{ label: string; count: number }>;
};

function countBy(values: Array<string | null | undefined>): Array<{ label: string; count: number }> {
  const counts = new Map<string, number>();
  for (const value of values) {
    const label = value?.trim() ? value.trim() : 'Unknown';
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

export function summarizeFlipkartCampaigns(
  campaigns: FlipkartAdsCampaign[]
): FlipkartCampaignSummary {
  return {
    total: campaigns.length,
    totalDailyBudget: campaigns.reduce(
      (sum, campaign) => sum + (campaign.daily_budget ?? 0),
      0
    ),
    byStatus: countBy(campaigns.map((campaign) => campaign.status)),
    byType: countBy(campaigns.map((campaign) => campaign.campaign_type)),
  };
}

export function formatFlipkartBudget(value: number | null | undefined): string {
  if (value == null) return '—';
  return value.toLocaleString();
}

export function formatFlipkartMoney(
  value: number | null | undefined,
  currency?: string | null
): string {
  if (value == null) return '—';
  const formatted = value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  });
  const code = (currency || '').trim();
  if (code && !/^\d+$/.test(code)) {
    return `${code} ${formatted}`;
  }
  return formatted;
}

export function formatFlipkartMatchType(matchType: string | null | undefined): string {
  const raw = (matchType || '').trim();
  if (!raw) return '—';
  const key = raw.toUpperCase();
  if (key === 'B' || key === 'P' || key === 'PHRASE') return 'Phrase';
  if (key === 'E' || key === 'EXACT') return 'Exact';
  if (key === 'BROAD') return 'Broad';
  return raw;
}

export function formatFlipkartPriceRange(
  minPrice: number | null | undefined,
  maxPrice: number | null | undefined,
  currency?: string | null
): string {
  if (minPrice == null && maxPrice == null) return '—';
  if (minPrice != null && maxPrice != null && minPrice !== maxPrice) {
    return `${formatFlipkartMoney(minPrice, currency)} – ${formatFlipkartMoney(maxPrice, currency)}`;
  }
  return formatFlipkartMoney(minPrice ?? maxPrice, currency);
}
